import { authClient } from "~/lib/auth-client"
import { textToEditorContent, getEditorPlainText } from "~/lib/utils"
import { useState } from "react"
import { useOnboardingData } from "~/hooks/useOnboardingData"
import { IssueStatus, ProjectStatus } from "~/lib/enums"
import { tutorials } from "~/lib/tutorials"
import type { RouterInputs, RouterOutputs } from "~/trpc/react"

export function useProjectPractice() {
	const seed = useOnboardingData()
	const { data: session } = authClient.useSession()
	const currentUser = {
		id: session?.user.id ?? "practice-user",
		name: session?.user.name ?? "Teammate",
		image: session?.user.image ?? null
	}
	const [createdAt] = useState(() => new Date())
	const [participants, setParticipants] = useState<
		RouterOutputs["projects"]["updates"]["participants"]
	>(() => [
		{
			id: "practice-self",
			userId: currentUser.id,
			name: currentUser.name,
			image: currentUser.image,
			included: true
		},
		{
			id: "practice-kevin",
			userId: "practice-kevin-user",
			name: "Kevin",
			image: null,
			included: true
		},
		{
			id: "practice-sam",
			userId: "practice-sam-user",
			name: "Sam",
			image: null,
			included: true
		}
	])
	const [updates, setUpdates] = useState<
		RouterOutputs["projects"]["updates"]["updates"]
	>(() => [
		{
			id: "practice-project-update-kevin",
			author: { id: "practice-kevin-user", name: "Kevin", image: null },
			content: textToEditorContent(tutorials["projects-v1"].practice.update),
			createdAt: new Date(createdAt.getTime() - 60 * 60 * 1000)
		}
	])
	const content = tutorials["projects-v1"].practice
	const [project, setProject] = useState<RouterOutputs["projects"]["get"]>(
		() => ({
			id: "practice-project",
			name: content.name,
			description: content.description,
			status: ProjectStatus.Planned,
			leadId: "practice-kevin",
			createdAt,
			startDate: createdAt,
			targetDate: null,
			resources: [{ title: content.resourceTitle, url: content.resourceUrl }],
			updatedAt: new Date()
		})
	)
	const [issues, setIssues] = useState(() =>
		content.issues.map((title, index) => ({
			...seed.issue,
			id: `practice-project-issue-${index}`,
			title,
			friendlyId: index + 10,
			status: index === 0 ? IssueStatus.Complete : IssueStatus.ToDo,
			projectId: index === 2 ? null : "practice-project",
			team: {
				...seed.team,
				organization: { name: "Biteclub", slug: "practice" }
			},
			assignee: null,
			labels: []
		}))
	)
	return {
		project,
		issues,
		createdAt,
		participants,
		updates,
		canPost: project.leadId === "practice-self",
		setParticipant: (input: RouterInputs["projects"]["setParticipant"]) =>
			setParticipants((current) =>
				current.map((member) =>
					member.id === input.teamMemberId
						? { ...member, included: input.included }
						: member
				)
			),
		postUpdate: async (input: RouterInputs["projects"]["postUpdate"]) => {
			if (project.leadId !== "practice-self")
				throw new Error("Only the project lead can post updates.")
			if (!getEditorPlainText(input.content).trim())
				throw new Error("Write an update before posting.")
			setUpdates((current) => [
				{
					id: crypto.randomUUID(),
					author: currentUser,
					content: input.content,
					createdAt: new Date()
				},
				...current
			])
		},
		save: async (values: RouterInputs["projects"]["save"]) => {
			if (
				values.targetDate &&
				values.targetDate < (values.startDate ?? createdAt)
			)
				throw new Error("The project due date cannot be before its start date.")
			setProject({
				...project,
				...values,
				startDate: values.startDate ?? createdAt,
				id: project.id,
				updatedAt: new Date()
			})
		},
		editIssue: (values: RouterInputs["issues"]["edit"]) =>
			setIssues((current) =>
				current.map((issue) =>
					issue.id === values.id
						? { ...issue, projectId: values.projectId ?? null }
						: issue
				)
			)
	}
}
